/**
 * PDP/PPF Export - Generates a ZIP bundle ready for deposit on a
 * Plateforme de Dematerialisation Partenaire or the Portail Public de Facturation.
 *
 * Contents:
 *  - {invoiceNumber}.pdf   (Factur-X hybrid PDF)
 *  - {invoiceNumber}.xml   (CII XML standalone)
 *  - metadata.json          (structured deposit metadata)
 *  - README_DEPOT.txt       (instructions for the operator)
 */

import JSZip from 'jszip';
import { generateInvoicePdf, InvoicePdfData } from './generateInvoicePdf';
import { FACTUR_X_PROFILE } from './facturXml';

export interface DepositMetadata {
  format: 'Factur-X';
  profile: typeof FACTUR_X_PROFILE;
  flavor: 'BASIC';
  specId: 'urn:cen.eu:en16931:2017#compliant#urn:factur-x.eu:1p0:basic';
  standard: 'EN 16931';
  xmlFormat: 'CII';
  pdfConformance: 'PDF/A-3B';
  invoiceNumber: string;
  issueDate: string;
  dueDate?: string;
  deliveryDate: string;
  currency: string;
  paymentTerms: string;
  seller: {
    name: string;
    siret: string;
    vatNumber: string;
    address: Record<string, string>;
    email: string;
  };
  buyer: {
    name: string;
    email: string;
    address: Record<string, string>;
  };
  totals: {
    totalHt: number;
    totalVat: number;
    totalTtc: number;
    duePayable: number;
  };
  lineCount: number;
  generatedAt: string;
  softwareName: string;
  softwareVersion: string;
}

function buildMetadata(data: InvoicePdfData): DepositMetadata {
  return {
    format: 'Factur-X',
    profile: FACTUR_X_PROFILE,
    flavor: 'BASIC',
    specId: 'urn:cen.eu:en16931:2017#compliant#urn:factur-x.eu:1p0:basic',
    standard: 'EN 16931',
    xmlFormat: 'CII',
    pdfConformance: 'PDF/A-3B',
    invoiceNumber: data.invoiceNumber,
    issueDate: data.issueDate,
    dueDate: data.dueDate,
    deliveryDate: data.issueDate,
    currency: 'EUR',
    paymentTerms: data.dueDate
      ? `Paiement du au ${data.dueDate}`
      : 'Paiement a reception de la facture',
    seller: {
      name: data.sellerName,
      siret: data.sellerSiret,
      vatNumber: data.sellerVatNumber,
      address: data.sellerAddress as Record<string, string>,
      email: data.sellerEmail,
    },
    buyer: {
      name: data.buyerName,
      email: data.buyerEmail,
      address: data.buyerAddress as Record<string, string>,
    },
    totals: {
      totalHt: data.totalHt,
      totalVat: data.totalVat,
      totalTtc: data.totalTtc,
      duePayable: data.totalTtc,
    },
    lineCount: data.items.length,
    generatedAt: new Date().toISOString(),
    softwareName: 'Consentink',
    softwareVersion: '2.0.0',
  };
}

const README_CONTENT = `=== DOSSIER D'EXPORT PDP/PPF ===

Ce dossier contient les fichiers necessaires au depot d'une facture
electronique sur une Plateforme de Dematerialisation Partenaire (PDP)
ou le Portail Public de Facturation (PPF).

Contenu :
  - [numero].pdf     - Facture PDF hybride au format Factur-X (PDF + XML embarque)
  - [numero].xml     - Fichier XML CII autonome (norme EN 16931)
  - metadata.json    - Metadonnees structurees du document
  - README_DEPOT.txt - Ce fichier

Format : Factur-X EN 16931 / Cross Industry Invoice

Instructions :
  1. Connectez-vous a votre plateforme PDP agreee ou au PPF (chorus-pro.gouv.fr)
  2. Importez le fichier PDF (Factur-X) OU le fichier XML selon les
     exigences de votre plateforme
  3. Verifiez que les metadonnees correspondent a votre comptabilite
  4. Validez le depot

Note : Le fichier metadata.json facilite l'integration automatisee
avec les logiciels de comptabilite et les API de depot.
`;

export async function generatePdpZip(data: InvoicePdfData): Promise<Blob> {
  const { pdfBlob, xmlString } = await generateInvoicePdf(data);
  const metadata = buildMetadata(data);

  const zip = new JSZip();
  const folderName = `depot_${data.invoiceNumber.replace(/\//g, '-')}`;
  const folder = zip.folder(folderName)!;

  folder.file(`${data.invoiceNumber}.pdf`, pdfBlob);
  folder.file(`${data.invoiceNumber}.xml`, xmlString);
  folder.file('metadata.json', JSON.stringify(metadata, null, 2));
  folder.file('README_DEPOT.txt', README_CONTENT);

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

export async function generateBatchPdpZip(invoices: InvoicePdfData[]): Promise<Blob> {
  const zip = new JSZip();
  const batchMeta: { exportedAt: string; count: number; invoices: string[] } = {
    exportedAt: new Date().toISOString(),
    count: invoices.length,
    invoices: [],
  };

  for (const data of invoices) {
    const { pdfBlob, xmlString } = await generateInvoicePdf(data);
    const metadata = buildMetadata(data);
    const safeNum = data.invoiceNumber.replace(/\//g, '-');
    const folder = zip.folder(safeNum)!;

    folder.file(`${safeNum}.pdf`, pdfBlob);
    folder.file(`${safeNum}.xml`, xmlString);
    folder.file('metadata.json', JSON.stringify(metadata, null, 2));

    batchMeta.invoices.push(data.invoiceNumber);
  }

  zip.file('README_DEPOT.txt', README_CONTENT);
  zip.file('batch_manifest.json', JSON.stringify(batchMeta, null, 2));

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}
